import { useState, useRef, useMemo } from 'react'
import {
  Box,
  Typography,
  ToggleButton,
  useTheme,
  Card,
  IconButton,
  ButtonGroup,
  Button
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import HideSourceIcon from '@mui/icons-material/HideSource'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useSmartTranslation } from './hooks/useSmartTranslation'

const TableTune = ({ title = '', fields, onChange = () => {} }) => {
  const theme = useTheme()
  const [dragIndex, setDragIndex] = useState(null)
  const initialFields = useRef(fields.map((f, i) => ({ ...f, order: i + 1 })))
  const allVisible = useMemo(() => fields.every(f => f.show), [fields])
  const allHidden = useMemo(() => fields.every(f => !f.show), [fields])
  const { t_ } = useSmartTranslation()
  const isChanged = useMemo(() => {
    return fields.some((f, i) =>
      f.name !== initialFields.current[i].name ||
      f.show !== initialFields.current[i].show
    )
  }, [fields])

  const handleToggle = (name) => {
    const next = fields
      .map(it => it.name === name ? { ...it, show: !it.show, order: !it.show ? it.order : Infinity } : it)
      .sort((a, b) => a.order - b.order)
      .map((item, index) => ({ ...item, order: index + 1 }))

    onChange(next)
  }

  const handleDragStart = (e, index) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return
    const reordered = [...fields]
    const [removed] = reordered.splice(dragIndex, 1)
    reordered.splice(index, 0, removed)
    setDragIndex(null)
    onChange(reordered.map((item, index) => ({ ...item, order: index + 1 })))
  }

  const handleToggleClick = (e, name) => {
    e.stopPropagation()
    handleToggle(name)
  }

  const handleShowAll = () => {
    const next = fields.map((item, index) => ({ ...item, show: true, order: index + 1 }))
    onChange(next)
  }

  const handleHideAll = () => {
    const next = fields.map((item, index) => ({ ...item, show: false, order: index + 1 }))
    onChange(next)
  }

  const handleRestore = () => {
    onChange(initialFields.current)
  }

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, borderRadius: 2, p: 2 }}>
      <Typography variant='subtitle1' sx={{ mb: 2, color: theme.palette.primary.main }}>
        {title}
      </Typography>

      <ButtonGroup size='small' sx={{ mb: 2 }}>
        <Button startIcon={<DoneAllIcon />} onClick={handleShowAll} disabled={allVisible}>
          {t_('Show all')}
        </Button>
        <Button startIcon={<HideSourceIcon />} onClick={handleHideAll} disabled={allHidden}>
          {t_('Hide all')}
        </Button>
        <Button startIcon={<RestartAltIcon />} onClick={handleRestore} disabled={!isChanged}>
          {t_('Restore')}
        </Button>
      </ButtonGroup>

      {fields.map((item, index) => (
        <Card
          key={item.name}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(index)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: theme.palette.background.paper,
            borderRadius: 1,
            p: 1,
            mb: 1,
            cursor: 'grab',
            '&:active': {
              cursor: 'grabbing'
            },
            '&:hover': {
              bgcolor: theme.palette.action.hover
            },
            transition: 'background-color 0.2s ease'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size='small'
              sx={{
                color: theme.palette.text.disabled,
                pointerEvents: 'none'
              }}
            >
              <DragIndicatorIcon fontSize='small' />
            </IconButton>

            <Typography variant='body2' color='text.secondary'>
              {item.label}
            </Typography>
          </Box>

          <ToggleButton
            value={item.name}
            selected={item.show}
            onChange={(e) => handleToggleClick(e, item.name)}
            aria-label={item.label}
            sx={{
              width: 28,
              height: 28,
              borderRadius: '4px',
              border: `1px solid ${item.show ? theme.palette.success.main : theme.palette.error.main}`,
              color: item.show ? theme.palette.success.main : theme.palette.error.main,
              '&.Mui-selected': {
                bgcolor: 'transparent',
                color: theme.palette.success.main
              },
              cursor: 'pointer !important'
            }}
          >
            {item.show ? <CheckIcon sx={{ fontSize: 18 }} /> : <CloseIcon sx={{ fontSize: 18 }} />}
          </ToggleButton>
        </Card>
      ))}
    </Box>
  )
}

export default TableTune
